import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Country, State } from "country-state-city";
import Header from "../headerFooter/header";
import HomeFooter from "../homepage/homefooter";
import styles from "./PurchaseFormPage.module.css";

const PurchaseFormPage = () => {
  const { state: locationState } = useLocation();
  const navigate = useNavigate();

  // Fallback values to prevent crash if accessed directly
  const { productName, price, quantity } = locationState || {
    productName: "Test Product",
    price: 0,
    quantity: 0,
  };
  const amount = price * quantity;

  // Initialize Select Options
  const countryOptions = Country.getAllCountries().map((c) => ({
    value: c.isoCode,
    label: c.name,
  }));
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    houseNo: "",
    street: "",
    city: "",
    pincode: "",
    landmark: "",
  });

  const [errors, setErrors] = useState({});

  // Dynamic State Options based on Country
  const stateOptions = selectedCountry
    ? State.getStatesOfCountry(selectedCountry.value).map((s) => ({
        value: s.isoCode,
        label: s.name,
      }))
    : [];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null })); // Clear error on type
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Full Name is required";
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Valid email is required";
    if (!formData.phone || formData.phone.length < 8)
      newErrors.phone = "Valid phone number is required";
    if (!formData.houseNo.trim()) newErrors.houseNo = "Address is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.pincode.trim()) newErrors.pincode = "Pincode is required";
    if (!selectedCountry) newErrors.country = "Country is required";
    if (!selectedState) newErrors.state = "State is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      navigate("/payment", {
        state: {
          formData: {
            ...formData,
            country: selectedCountry?.label,
            state: selectedState?.label,
          },
          productName,
          amount,
          quantity,
        },
      });
    } else {
      // Scroll to top to show errors
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.container}>
        <Header />
        <form
          onSubmit={handleSubmit}
          className={styles.formContainer}
          noValidate
        >
          <div className={styles.header}>
            <h2>Shipping Details</h2>
            <div className={styles.summary}>
              <span>
                Item: <strong>{productName}</strong>
              </span>
              <span>
                Total: <strong>₹{amount}</strong>
              </span>
            </div>
          </div>

          {/* Full Name */}
          <div className={styles.inputGroup}>
            <label className={styles.label}>Full Name</label>
            <input
              name="name"
              type="text"
              className={styles.input}
              placeholder="e.g. Aditi Sharma"
              value={formData.name}
              onChange={handleInputChange}
            />
            {errors.name && (
              <span className={styles.errorMsg}>{errors.name}</span>
            )}
          </div>

          {/* Email */}
          <div className={styles.inputGroup}>
            <label className={styles.label}>Email Address</label>
            <input
              name="email"
              type="email"
              className={styles.input}
              placeholder="name@example.com"
              value={formData.email}
              onChange={handleInputChange}
            />
            {errors.email && (
              <span className={styles.errorMsg}>{errors.email}</span>
            )}
          </div>

          {/* Phone - Full Width */}
          <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
            <label className={styles.label}>Phone Number</label>
            <PhoneInput
              country={"in"}
              value={formData.phone}
              onChange={(phone) => setFormData({ ...formData, phone })}
              enableSearch
            />
            {errors.phone && (
              <span className={styles.errorMsg}>{errors.phone}</span>
            )}
          </div>

          {/* Address Line 1 */}
          <div className={styles.inputGroup}>
            <label className={styles.label}>House / Flat No.</label>
            <input
              name="houseNo"
              className={styles.input}
              placeholder="e.g. A-104"
              value={formData.houseNo}
              onChange={handleInputChange}
            />
            {errors.houseNo && (
              <span className={styles.errorMsg}>{errors.houseNo}</span>
            )}
          </div>

          {/* Address Line 2 */}
          <div className={styles.inputGroup}>
            <label className={styles.label}>Street / Colony</label>
            <input
              name="street"
              className={styles.input}
              placeholder="e.g. Green Park"
              value={formData.street}
              onChange={handleInputChange}
            />
          </div>
          
           {/* Landmark */}
          <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
            <label className={styles.label}>Landmark (Optional)</label>
            <input
              name="landmark"
              className={styles.input}
              placeholder="Near City Hospital"
              value={formData.landmark}
              onChange={handleInputChange}
            />
          </div>

          {/* City */}
          <div className={styles.inputGroup}>
            <label className={styles.label}>City</label>
            <input
              name="city"
              className={styles.input}
              placeholder="City Name"
              value={formData.city}
              onChange={handleInputChange}
            />
            {errors.city && (
              <span className={styles.errorMsg}>{errors.city}</span>
            )}
          </div>

          {/* Pincode */}
          <div className={styles.inputGroup}>
            <label className={styles.label}>Pincode</label>
            <input
              name="pincode"
              className={styles.input}
              placeholder="Postal Code"
              value={formData.pincode}
              onChange={handleInputChange}
            />
            {errors.pincode && (
              <span className={styles.errorMsg}>{errors.pincode}</span>
            )}
          </div>

          {/* Country Select */}
          <div className={styles.inputGroup}>
            <label className={styles.label}>Country</label>
            <Select
              options={countryOptions}
              value={selectedCountry}
              onChange={(val) => {
                setSelectedCountry(val);
                setSelectedState(null);
              }}
              placeholder="Select Country"
            />
            {errors.country && (
              <span className={styles.errorMsg}>{errors.country}</span>
            )}
          </div>

          {/* State Select */}
          <div className={styles.inputGroup}>
            <label className={styles.label}>State</label>
            <Select
              options={stateOptions}
              value={selectedState}
              onChange={setSelectedState}
              placeholder="Select State"
              isDisabled={!selectedCountry}
              noOptionsMessage={() => "Select a country first"}
            />
            {errors.state && (
              <span className={styles.errorMsg}>{errors.state}</span>
            )}
          </div>

         

          <button type="submit" className={styles.submitButton}>
            Proceed to Secure Payment
          </button>
        </form>
        <HomeFooter />
      </div>
    </div>
  );
};

export default PurchaseFormPage;
